import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';


export interface DashboardProject {
    id: number;
    name: string;
    businessUnitCode: string;
    businessUnitName: string;
    wellName: string;
    wellId: number;
    jobType: string;
    jobSubType: string;
    status: string;
    receiveDate: string;
    expectedDueDate: string;
    isBlendTest: boolean;
    measuredDepth: number | null;
    totalVerticalDepth: number | null;
    mudType: string;
    notes: string;
    receivedBy: string;
    sentToLabDate: string | null;
    rAndDProject: boolean;
}

export interface DashboardResponse {
    year: number;
    totalCount: number;
    dashboardType: string;
    projects: DashboardProject[];
}

export interface ProjectSummary {
    id: number;
    name: string;
    sapCode: string;
    receivedBy: string;
    receiveDate: string;
    sbuCode: string;
    sbuName: string;
    rig: string;
    wellName: string;
    payGo: boolean;
    pipeSize: string | null;
    jobType: string;
    jobSubType: string;
    cementMD: number;
    cementTVD: number;
    wellID: number;
    statusId: number;
    status: string;
    notes: string;
    rAndDProject: boolean;
}

export interface DashboardTest {
    testId: number;
    testType: number;
    testTypeName: string;
    innerTestType: number;
    testStatus: number;
    isMuted: boolean;
    projectId: number;
    projectName: string;
    projectStatus: number;
    slurryId: number;
    slurryName: string;
    wellId: number;
    wellName: string;
    rigName: string;
    businessUnitCode: string;
    requestEngineer: string;
    reviewedBy: string | null;
    sentToLabDate: string | null;
    lastUpdateDate: string | null;
    isSlurry: boolean;
    rAndDTest: boolean;
}



@Injectable({
    providedIn: 'root'
})
export class ProjectsDashboardService {

    private readonly baseUrl = environment.apiUrl;

    constructor(private http: HttpClient) { }

    getHomeDashboard(year: number): Observable<DashboardResponse> {
        return this.http.get<DashboardResponse>(`${this.baseUrl}/project-dashboard/project`, {
            params: { year: year.toString(), type: 'home' }
        });
    }

    getProjectsByYear(year: number): Observable<DashboardProject[]> {
        return this.http.get<DashboardProject[]>(`${this.baseUrl}/project-dashboard/project/${year}`);
    }

    getProjectsByUnit(year: number, unitCode: string): Observable<DashboardProject[]> {
        return this.http.get<DashboardProject[]>(`${this.baseUrl}/project-dashboard/project/unit/${year}/${unitCode}`);
    }

    getProjectsByWell(year: number, wellId: number): Observable<DashboardProject[]> {
        return this.http.get<DashboardProject[]>(`${this.baseUrl}/project-dashboard/project/well/${year}/${wellId}`);
    }

    getProjectSummaryByYear(year: number): Observable<ProjectSummary[]> {
        return this.http.get<ProjectSummary[]>(`${this.baseUrl}/project-dashboard/project-summary/${year}`);
    }

    getProjectSummaryByUnit(year: number, unitCode: string): Observable<ProjectSummary[]> {
        return this.http.get<ProjectSummary[]>(`${this.baseUrl}/project-dashboard/project-summary/unit/${year}/${unitCode}`);
    }

    getProjectSummaryByWell(year: number, wellId: number): Observable<ProjectSummary[]> {
        return this.http.get<ProjectSummary[]>(`${this.baseUrl}/project-dashboard/project-summary/well/${year}/${wellId}`);
    }

    getTests(year: number, wellId?: number, unitCode?: string): Observable<DashboardTest[]> {
        const params: Record<string, string> = { year: year.toString() };
        if (wellId !== undefined) params['wellId'] = wellId.toString();
        if (unitCode) params['unitCode'] = unitCode;
        return this.http.get<DashboardTest[]>(`${this.baseUrl}/project-dashboard/test-dashboard`, { params });
    }

    getNotes(): Observable<any> {
        return this.http.get<any>(`${this.baseUrl}/project-dashboard/notes`);
    }
}
